/**
 * "Testar uma chave" (docs/refactor/26-auditoria-08-10.md, item 1): paste the key your agent uses — the one
 * saved on the computer (save-key), or in the editor's MCP settings — and see at once whether Vephus accepts it, and if not why:
 * revoked, expired, of another organization, or not a key. The key goes to the server to be checked and is not
 * kept; nothing here counts as the agent using it.
 */

import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { CheckCircle2, CircleAlert } from 'lucide-react'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { formatDate, formatRelative } from '../lib/format'

export function KeyCheck() {
  const { t, locale } = useI18n()
  const [key, setKey] = useState('')
  const check = useMutation({ mutationFn: (value: string) => api.me.checkKey(value) })
  const result = check.data
  const ok = result?.state === 'accepted'

  return (
    <div className="stack stack--tight key-check">
      <span className="field__label">{t('keyCheck.title')}</span>
      <span className="tiny muted">{t('keyCheck.hint')}</span>
      <form
        className="row row--tight row--wrap"
        onSubmit={(event) => {
          event.preventDefault()
          if (key.trim()) check.mutate(key.trim())
        }}
      >
        <input
          className="input key-check__input mono"
          type="password"
          autoComplete="off"
          spellCheck={false}
          value={key}
          onChange={(event) => setKey(event.target.value)}
          placeholder="dpk_…"
          aria-label={t('keyCheck.input')}
          maxLength={300}
        />
        <button type="submit" className="btn btn--sm" disabled={!key.trim() || check.isPending}>{t('keyCheck.test')}</button>
      </form>
      {result && (
        <div className={`key-check__result small ${ok ? 'is-ok' : 'is-bad'}`} role="status">
          {ok ? <CheckCircle2 size={14} aria-hidden /> : <CircleAlert size={14} aria-hidden />}
          <span className="stack" style={{ gap: 2 }}>
            <strong>{t(`keyCheck.state.${result.state}`)}</strong>
            {result.name && (
              <span className="tiny">
                {t('keyCheck.which', { name: result.name, who: result.mine ? t('keyCheck.you') : result.developer ?? '?' })}
                {result.lastUsedAt ? ` · ${t('remote.keys.used', { when: formatRelative(result.lastUsedAt, locale) })}` : ` · ${t('remote.keys.neverUsed')}`}
                {result.expiresAt ? ` · ${t('remote.keys.expires', { date: formatDate(result.expiresAt, locale) })}` : ''}
                {result.revokedReason ? ` · ${result.revokedReason}` : ''}
              </span>
            )}
            {!ok && <span className="tiny">{t('keyCheck.fix')}</span>}
            {ok && !result.mine && <span className="tiny">{t('keyCheck.notYours')}</span>}
          </span>
        </div>
      )}
      {check.isError && <span className="field__error">{t('error.generic')}</span>}
    </div>
  )
}
